import { createContext, useContext } from "react";
import type { ApprovalCard, PanelEntry } from "../lib/api";

export interface OwnerPending {
  state: "reading" | "ready" | "unavailable";
  /** Only confirmed active requests are counted. An unread source has no number. */
  count?: number;
  /** A hash within this Agent, carrying the exact request or approval identity. */
  href?: string;
}

export function pendingInteractionPanels(panels: PanelEntry[]): PanelEntry[] {
  const seen = new Set<string>();
  return panels.filter(panel => {
    const request = panel.interaction;
    if (panel.audience !== "owner" || !request?.request_id || !request.session_id
      || (request.status ?? "pending") !== "pending" || seen.has(request.request_id)) return false;
    seen.add(request.request_id);
    return true;
  });
}

export function responseConversationHref(source: { session_id: string; owner_conversation?: boolean }, target: { request?: string; approval?: string }): string {
  return `#/chat?${new URLSearchParams({ session: source.owner_conversation === false ? "s-owner" : source.session_id || "s-owner", ...target })}`;
}

/** The same confirmed requests feed local navigation and the Hub's per-Agent badges. */
export function ownerPending(panels: PanelEntry[] | undefined, approvals: ApprovalCard[] | null | undefined, state: OwnerPending["state"]): OwnerPending {
  if (state !== "ready") return { state };
  if (!panels || !approvals) return { state: "reading" };
  const requests = pendingInteractionPanels(panels);
  const seen = new Set<string>();
  const pending = approvals.filter(card => {
    if (!card.key || card.resolved || seen.has(card.key)) return false;
    seen.add(card.key);
    return true;
  });
  const request = requests[0]?.interaction;
  const approval = pending[0];
  return { state: "ready", count: requests.length + pending.length,
    href: request ? responseConversationHref(request, { request: request.request_id })
      : approval ? responseConversationHref(approval, { approval: approval.key }) : undefined };
}

/**
 * What the sidebar shows about the signed-in account and cannot read for itself. The Hub's directory
 * page provides it around the frame; a shell that provides nothing gets a sidebar with the same
 * rows and none of these (no count, no credit, no sign-out).
 */
export interface OwnerShell {
  /** Each Agent's confirmed requests and read state; never inferred from recent conversation options. */
  pendingByAgent?: Record<string, OwnerPending>;
  /** What the account can still spend, already written ("¥27.86"); absent when the Hub did not say. */
  credit?: string;
  /** Signs this browser out of the Hub. Absent where there is no Hub account to leave. */
  onLogout?: () => void;
}

export const OwnerShellContext = createContext<OwnerShell>({});
export const useOwnerShell = () => useContext(OwnerShellContext);
